import { useMemo, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AqiDisplay from "./AqiDisplay";
import GoToStationButton from "./GoToStationButton";
import { RiRadioButtonLine } from "react-icons/ri";
import { MdCheckBox } from "react-icons/md";
import { MdCheckBoxOutlineBlank } from "react-icons/md";

const Stations = ({ AQIType }) => {
  const login = useSelector((state) => state.login);
  const isCompany = login.isCompany;
  const isLoggedIn = login.accessToken;

  const stationsHourly = useSelector((state) => state.stationsHourly);
  const stationsHourlyData = stationsHourly.stationsData;
  const stationsHourlyLoading = stationsHourly.stationsLoading;
  const stationsHourlyError = stationsHourly.stationsError;

  const stationsDaily = useSelector((state) => state.stationsDaily);
  const stationsDailyData = stationsDaily.stationsData;
  const stationsDailyLoading = stationsDaily.stationsLoading;
  const stationsDailyError = stationsDaily.stationsError;

  const stationsTwelve = useSelector((state) => state.stationsTwelve);
  const stationsTwelveData = stationsTwelve.stationsData;
  const stationsTwelveLoading = stationsTwelve.stationsLoading;
  const stationsTwelveError = stationsTwelve.stationsError;

  const stationsTwentyFour = useSelector((state) => state.stationsTwentyFour);
  const stationsTwentyFourData = stationsTwentyFour.stationsData;
  const stationsTwentyFourLoading = stationsTwentyFour.stationsLoading;
  const stationsTwentyFourError = stationsTwentyFour.stationsError;

  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationLoading = information.informationLoading;
  const informationError = information.informationError;

  const [theStations, setTheStations] = useState();
  const [stationsLoading, setStationsLoading] = useState();
  const [stationsError, setStationsError] = useState();

  // Memoize device information retrieval function
  const GetInformationOfTheDevice = useCallback(
    ({ deviceType, deviceId }) => {
      const theSpecificStation =
        informationData.find(
          (x) =>
            x.device === String(deviceType) && x.device_id === Number(deviceId)
        ) || {};

      return {
        device_id: theSpecificStation.device_id,
        device: theSpecificStation.device,
        is_kit: theSpecificStation.is_kit,
        is_test: theSpecificStation.is_test,
        is_opm: theSpecificStation.is_opm,
        user: theSpecificStation.user,
        province: theSpecificStation.province,
        city: theSpecificStation.city,
        stationName: theSpecificStation.stationName,
        longitude: theSpecificStation.longitude,
        latitude: theSpecificStation.latitude,
        slug: theSpecificStation.slug,
      };
    },
    [informationData]
  );

  // Memoize modified data for each station type and time interval
  const modifiedHourlyStationsList = useMemo(() => {
    const modifyStationData = (stationsData, deviceType) =>
      stationsData[deviceType]?.map((element) => ({
        ...element,
        ...GetInformationOfTheDevice({
          deviceType,
          deviceId: element.device_id,
        }),
      })) || [];

    return [
      ...modifyStationData(stationsHourlyData, "SAPA"),
      ...modifyStationData(stationsHourlyData, "SATA"),
      ...modifyStationData(stationsHourlyData, "ESPS"),
      ...modifyStationData(stationsHourlyData, "OSPM"),
    ];
  }, [stationsHourlyData, GetInformationOfTheDevice]);

  const modifiedDailyStationsList = useMemo(() => {
    const modifyStationData = (stationsData, deviceType) =>
      stationsData[deviceType]?.map((element) => ({
        ...element,
        ...GetInformationOfTheDevice({
          deviceType,
          deviceId: element.device_id,
        }),
      })) || [];

    return [
      ...modifyStationData(stationsDailyData, "SAPA"),
      ...modifyStationData(stationsDailyData, "SATA"),
      ...modifyStationData(stationsDailyData, "ESPS"),
      ...modifyStationData(stationsDailyData, "OSPM"),
    ];
  }, [stationsDailyData, GetInformationOfTheDevice]);

  const modifiedTwelveStationsList = useMemo(() => {
    const modifyStationData = (stationsData, deviceType) =>
      stationsData[deviceType]?.map((element) => ({
        ...element,
        ...GetInformationOfTheDevice({
          deviceType,
          deviceId: element.device_id,
        }),
      })) || [];

    return [
      ...modifyStationData(stationsTwelveData, "SAPA"),
      ...modifyStationData(stationsTwelveData, "SATA"),
      ...modifyStationData(stationsTwelveData, "ESPS"),
      ...modifyStationData(stationsTwelveData, "OSPM"),
    ];
  }, [stationsTwelveData, GetInformationOfTheDevice]);

  const modifiedTwentyFourStationsList = useMemo(() => {
    const modifyStationData = (stationsData, deviceType) =>
      stationsData[deviceType]?.map((element) => ({
        ...element,
        ...GetInformationOfTheDevice({
          deviceType,
          deviceId: element.device_id,
        }),
      })) || [];

    return [
      ...modifyStationData(stationsTwentyFourData, "SAPA"),
      ...modifyStationData(stationsTwentyFourData, "SATA"),
      ...modifyStationData(stationsTwentyFourData, "ESPS"),
      ...modifyStationData(stationsTwentyFourData, "OSPM"),
    ];
  }, [stationsTwentyFourData, GetInformationOfTheDevice]);

  // Memoize sorting function
  const PersianSortComparer = useCallback((a, b) => {
    const compare = (field) => (a[field] || "").localeCompare(b[field] || "");
    return compare("province") || compare("city") || compare("stationName");
  }, []);

  // Memoize station information retrieval function
  const getInformationOfStations = useCallback(() => {
    let sortedData;
    let loading;
    let error;

    if (AQIType === "hourly") {
      sortedData = modifiedHourlyStationsList;
      loading = stationsHourly.stationsLoading;
      error = stationsHourly.stationsError;
    } else if (AQIType === "daily") {
      sortedData = modifiedDailyStationsList;
      loading = stationsDaily.stationsLoading;
      error = stationsDaily.stationsError;
    } else if (AQIType === "twelve") {
      sortedData = modifiedTwelveStationsList;
      loading = stationsTwelve.stationsLoading;
      error = stationsTwelve.stationsError;
    } else if (AQIType === "twentyFour") {
      sortedData = modifiedTwentyFourStationsList;
      loading = stationsTwentyFour.stationsLoading;
      error = stationsTwentyFour.stationsError;
    }

    setTheStations(sortedData ? [...sortedData].sort(PersianSortComparer) : []);
    setStationsLoading(loading);
    setStationsError(error);
  }, [
    AQIType,
    modifiedHourlyStationsList,
    modifiedDailyStationsList,
    modifiedTwelveStationsList,
    modifiedTwentyFourStationsList,
    PersianSortComparer,
  ]);

  useEffect(() => {
    getInformationOfStations();
  }, [
    AQIType,
    stationsHourlyLoading,
    stationsDailyLoading,
    stationsTwelveLoading,
    stationsTwentyFourLoading,
    stationsHourlyError,
    stationsDailyError,
    stationsTwelveError,
    stationsTwentyFourError,
    informationLoading,
    informationError,
  ]);

  const convertToJalali = useCallback((x) => {
    const A = x.slice(0, 10);
    const B = x.slice(11, 19);
    const C = new Date(A);
    const D = new Intl.DateTimeFormat("fa-IR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(C);
    return `${D} (${B})`;
  }, []);

  const convertToJalaliDate = useCallback((x) => {
    const A = x.slice(0, 10);
    const C = new Date(A);
    const D = new Intl.DateTimeFormat("fa-IR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(C);
    return D;
  }, []);

  useEffect(() => {}, [isLoggedIn, isCompany]);

  if (stationsLoading || informationLoading) {
    return null;
  } else if (stationsError || stationsError) {
    return null;
  } else if (theStations && theStations.length > 0) {
    return (
      <div id="stationTable" className="container-fluid g-0 gap-0">
        <table className="table table-light table-striped table-responsive overflow-scroll">
          <thead>
            <tr>
              {isCompany && (
                <>
                  <th className="text-center px-md-3 IransansXE">OPM</th>
                  <th className="text-center px-md-3 IransansXE">سریال</th>
                  <th className="text-center px-md-3 IransansXE">دستگاه</th>
                </>
              )}
              <th className="text-center px-md-5 d-none d-md-table-cell IransansXE">
                استان
              </th>
              <th className="text-center px-md-5 d-none d-md-table-cell IransansXE">
                شهر
              </th>
              <th className="text-center px-md-3 IransansXE">نام ایستگاه</th>
              {isCompany && (
                <th className="text-center px-md-3 IransansXE">آخرین داده</th>
              )}
              <th className="text-center px-md-3 IransansXE">AQI</th>
              <th className="text-center px-md-5"></th>
            </tr>
          </thead>
          <tbody>
            {theStations.map((station) => (
              <tr key={station.slug}>
                {!station.is_test &&
                  station.province &&
                  station.city &&
                  station.stationName && (
                    <>
                      {isCompany && (
                        <>
                          <td className="text-center text-large px-md-3 align-baseline">
                            {station.is_opm ? (
                              <MdCheckBox />
                            ) : (
                              <MdCheckBoxOutlineBlank />
                            )}
                          </td>

                          <td className="text-center px-md-3 IransansXE ss01 align-baseline">
                            {station.device_id}
                          </td>
                          <td className="text-center px-md-3 IransansXE ss01 align-baseline">
                            {station.device}
                          </td>
                        </>
                      )}
                      <td className="text-center px-md-5 align-baseline d-none d-md-table-cell">
                        {station.province}
                      </td>
                      <td className="text-center px-md-5 align-baseline d-none d-md-table-cell">
                        {station.city}
                      </td>
                      <td className="text-center px-md-3 align-baseline">
                        {station.stationName}
                      </td>
                      {isCompany && (
                        <>
                          {AQIType === "daily" ? (
                            <td className="text-center px-md-3 IransansXE ss01">
                              {convertToJalaliDate(station.ccreated_datetime)}
                            </td>
                          ) : (
                            <td className="text-center px-md-3 IransansXE ss01">
                              {convertToJalali(station.ccreated_datetime)}
                            </td>
                          )}
                        </>
                      )}
                      <td className="text-center px-md-3 align-baseline">
                        {station.device === "SATA" &&
                        station.device_id === 102 ? (
                          <AqiDisplay
                            AQI={Math.max(
                              station.PM25AQI || 0,
                              station.PM10AQI || 0
                            )}
                          />
                        ) : (
                          <AqiDisplay AQI={station.AQI || 0} />
                        )}
                      </td>
                      <td className="text-center px-md-5 align-baseline">
                        <GoToStationButton theRouteUrl={station.slug} />
                      </td>
                    </>
                  )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
};

export default Stations;
